import { LineRecord, NodeState, SetOperation } from "@/components/FlowSheetEditor/services/modal"
import { IManualInputVar, getUnitAttributes } from "./attributes"
import { InputNumber, Select, Space } from "antd"
import { useCallback, useMemo, useRef, useState } from "react"
import { useGetDimUnits } from "@/pages/Project/hooks/useDimUnits"
import { useGetUnitEntity } from "@/pages/Project/hooks/useUnitEntity"
import { numberFixToStr, toDefaultUnitVal, toUnitVal, toUnitValFromUnit } from "@/components/FlowSheetEditor/components/RightPanel/UnitPanel/components/common/utils"
import { Dimension } from "@/services/dimension/dimension.model"
import { CompositionCellEditor } from "./CompositionCellEditor"
import { isArray } from "lodash";

interface VariableValue {
  attribute: string
  value?: number
  unitId: string
  displayValue?: string
}

interface VariableEditorProps {
  equipment: NodeState | LineRecord | SetOperation
  value?: VariableValue
  attributes: IManualInputVar[]
  onChange?: (val: VariableValue) => void
}

function toEditVal(rowValue: VariableValue | undefined): VariableValue {
  if (!rowValue) return {value: undefined, unitId: '', attribute: ''}
  const value = rowValue.displayValue ? Number(rowValue.displayValue) : rowValue.value
  return {...rowValue, value: value  }
}

export const VariableEditor = (props: VariableEditorProps) => {
  const {equipment, value: initValue, onChange, attributes} = props
  const getUnits = useGetDimUnits()
  const getUnitEntity = useGetUnitEntity()
  const varUnitRef = useRef<Dimension.Unit | undefined>()
  const [varKey, setVarKey] = useState<string>(initValue?.attribute || '')

  const [rowData, setRowData] = useState(toEditVal(initValue))
  const selectVar = useMemo(() => {
    if (!varKey) return;
    return attributes.find(item => item.value === varKey)
  }, [varKey, attributes])

  const changeValue = useCallback((partVal) => {
    const newVal = {...rowData, unitId: equipment.id, ...partVal}
    const dimCode = selectVar?.dimCode
    setRowData(newVal)
    const outVal = {...newVal}
    if (dimCode) {
      const unitEntity = getUnitEntity(selectVar.dimCode)
      if (varUnitRef.current?.id && varUnitRef.current?.id !== unitEntity?.unit.id) {
        const unitVal = toUnitValFromUnit(outVal.value, varUnitRef.current!, unitEntity?.unit!);
        outVal.displayValue = numberFixToStr(
          unitVal,
          unitEntity?.cutoff
        )
        outVal.value = toDefaultUnitVal(unitVal, unitEntity?.unit)
      } else {
        outVal.displayValue = outVal.value
        outVal.value = toDefaultUnitVal(outVal.value, unitEntity?.unit)
      }
    }
    onChange?.(outVal)
  }, [rowData, setRowData, onChange, equipment, selectVar, varUnitRef])


  const addenAfter = useMemo(() => {
    if (!selectVar || !selectVar.dimCode) {
      return null
    }
    const units = getUnits(selectVar.dimCode)
    const unitOptions = units.map(item => {
      return {label: item.code, value: item.id}
    })
    const unitEntity = getUnitEntity(selectVar.dimCode)
    varUnitRef.current = unitEntity?.unit
    return <Select defaultValue={unitEntity?.unit.id} style={{width: 70}} options={unitOptions} onChange={(id) => {
      varUnitRef.current = units.find(item => item.id === id)
      changeValue({})
    }}/>
  }, [selectVar, initValue, changeValue, getUnitEntity, getUnits])

  const editor = () => {
    if (varKey.endsWith('xi_$1')) {
      const valueValid = isArray(rowData.value)
      return <CompositionCellEditor
        unitId={rowData.unitId as string}
        attribute={rowData.attribute}
        value={valueValid ? rowData.value as any : undefined}
        onChange={(val) => {
        changeValue({value: val})
      }}/>
    }
    return <InputNumber disabled={!equipment} type="number" addonAfter={addenAfter} value={rowData.value} onChange={(val) => {
      changeValue({value: val})
    }}/>
  }

  return <Space>
    <Select
      options={attributes}
      showSearch optionFilterProp='label'
      disabled={!equipment}
      style={{width: 140}}
      value={varKey}
      onSelect={(val) => {
        setVarKey(val)
        changeValue({attribute: val})
      }}
    />
    {editor()}
  </Space>
}
